import { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Users, Gift, ArrowUpRight, Loader2 } from "lucide-react";
import { getAdminSales } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (n, ccy = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n || 0);

const relTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-secondary/40 p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
    <div className="mt-1 font-serif text-2xl font-bold">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export const AdminSalesCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    getAdminSales()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <div data-testid="admin-sales-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <DollarSign className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Admin — Sales</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Live view of your Lectura revenue. Only visible to admins.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 grid place-items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-accent" />
        </div>
      ) : !data ? (
        <p className="mt-6 text-sm text-muted-foreground text-center py-8">
          Couldn't load sales — try refreshing.
        </p>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testid="stat-lifetime-revenue"
                  label="Lifetime revenue"
                  value={money(data.lifetime.revenue)}
                  sub={`${data.lifetime.count} sale${data.lifetime.count === 1 ? "" : "s"}`} />
            <Stat testid="stat-this-month"
                  label="This month"
                  value={money(data.this_month.revenue)}
                  sub={`${data.this_month.count} sold`} />
            <Stat testid="stat-this-week"
                  label="Last 7 days"
                  value={money(data.this_week.revenue)}
                  sub={`${data.this_week.count} sold`} />
            <Stat testid="stat-mrr"
                  label="MRR (estimate)"
                  value={money(data.mrr_estimate)}
                  sub="active passes / 12" />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div data-testid="mix-monthly-yearly" className="rounded-xl border border-border bg-secondary/40 p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Plan mix</div>
              <div className="mt-2 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>Monthly</span><span className="font-semibold">{data.mix.monthly_count}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span>Yearly</span><span className="font-semibold">{data.mix.yearly_count}</span>
                  </div>
                </div>
              </div>
            </div>
            <div data-testid="mix-gift-self" className="rounded-xl border border-border bg-secondary/40 p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-1.5">
                <Gift className="h-3 w-3" /> Gift vs self
              </div>
              <div className="mt-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Self</span><span className="font-semibold">{data.mix.self_count}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span>Gifts</span><span className="font-semibold">{data.mix.gift_count}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3">
            <TrendingUp className="h-4 w-4 text-accent shrink-0" />
            <span className="text-sm">
              <span className="font-semibold">{money(data.referral_revenue)}</span> from referred users so far — that's viral revenue you didn't pay ad spend for.
            </span>
          </div>

          <h4 className="font-serif text-lg font-bold mt-6 mb-2">Recent sales</h4>
          {data.recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No sales yet — as soon as your first customer buys, they'll show up here in real time.
            </p>
          ) : (
            <ol className="divide-y divide-border">
              {data.recent.map((r, i) => (
                <li key={i} data-testid={`sale-row-${i}`} className="py-3 flex items-center gap-3">
                  <span className={`grid place-items-center h-8 w-8 rounded-full text-xs font-bold shrink-0 ${r.gift_to ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"}`}>
                    {r.gift_to ? "🎁" : "💳"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                      {r.buyer_name || r.buyer_email || "Unknown buyer"}
                      {r.referred && <span className="rounded-full bg-accent/15 text-accent text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wider">Referred</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.plan === "pro_yearly" ? "Yearly Pass" : "Monthly Pass"}
                      {r.gift_to && <span> · gifted to {r.gift_to}</span>}
                      {" · "}{relTime(r.created_at)}
                    </div>
                  </div>
                  <div className="font-serif font-bold text-sm shrink-0">
                    {money(r.amount, r.currency)}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <a
            href="https://dashboard.stripe.com/payments"
            target="_blank"
            rel="noreferrer"
            data-testid="stripe-dashboard-link"
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition"
          >
            Open full Stripe Dashboard <ArrowUpRight className="h-4 w-4" />
          </a>
        </>
      )}
    </div>
  );
};
